/**
 * Content Context
 *
 * Provides access to editable content throughout the application.
 * Fetches content from the public API on mount and makes it available to all components.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import type {
  Package,
  AdditionalService,
  Testimonial,
  FAQ,
  DoulaProfile,
  PartnerProfile,
  SiteSettings,
} from "@shared/types/content";

interface ContentData {
  packages: Package[];
  "additional-services": AdditionalService[];
  testimonials: Testimonial[];
  faqs: FAQ[];
  "doula-profiles": DoulaProfile[];
  "partner-profiles": PartnerProfile[];
  "site-settings": SiteSettings;
}

interface ContentContextType {
  content: ContentData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ContentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/content");

      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.statusText}`);
      }

      const data = await response.json();
      setContent(data);
    } catch (err: any) {
      console.error("Error fetching content:", err);
      setError(err.message || "Failed to load content");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return (
    <ContentContext.Provider
      value={{
        content,
        isLoading,
        error,
        refetch: fetchContent,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

/**
 * Hook to access content throughout the application
 */
export function useContent() {
  const context = useContext(ContentContext);

  if (context === undefined) {
    throw new Error("useContent must be used within a ContentProvider");
  }

  return context;
}

/**
 * Helper hooks for specific content types
 */
// Fallback packages when API is unavailable
const FALLBACK_PACKAGES: import("@shared/types/content").Package[] = [
  {
    id: "1",
    title: "The Quickening",
    description: "Perfect for those who want essential doula support",
    includes: [
      "Birth Harmony Mini Course",
      "Two prenatal visits",
      "On-call support from 38 weeks",
      "Labor and birth support",
      "One postpartum visit",
    ],
    investment: "$1,200 - $1,800",
    additionalInfo: "Sliding scale pricing available",
  },
  {
    id: "2",
    title: "The Momentous Birth",
    description: "Our most popular comprehensive package",
    includes: [
      "Full Birth Harmony Course ($1,500 value)",
      "Four prenatal visits",
      "On-call support from 37 weeks",
      "Continuous labor and birth support",
      "Two postpartum visits",
      "Placenta encapsulation coordination",
    ],
    investment: "$2,400 - $3,200",
    additionalInfo: "Sliding scale pricing available",
    badge: "BEST VALUE",
  },
  {
    id: "3",
    title: "The Sovereign Birth",
    description: "Premium support for your complete journey",
    includes: [
      "Full Birth Harmony Course ($1,500 value)",
      "Six prenatal visits",
      "On-call support from 36 weeks",
      "Continuous labor and birth support",
      "Four postpartum visits",
      "Meal train coordination",
      "Placenta encapsulation",
    ],
    investment: "$3,600 - $4,800",
    additionalInfo: "Sliding scale pricing available",
  },
];

export function usePackages() {
  const { content } = useContent();
  return content?.packages?.length ? content.packages : FALLBACK_PACKAGES;
}

export function useAdditionalServices() {
  const { content } = useContent();
  return content?.["additional-services"] || [];
}

// Fallback testimonials when API is unavailable
const FALLBACK_TESTIMONIALS: import("@shared/types/content").Testimonial[] = [
  {
    id: "1",
    name: "Sarah M.",
    quote:
      "Samantha helped me have the peaceful, empowering birth I always dreamed of. Her hypnobirthing techniques made all the difference.",
    rating: 5,
    tag: "Home Birth",
  },
  {
    id: "2",
    name: "Emily R.",
    quote:
      "I was so anxious about birth before taking the Birth Harmony course. Now I feel confident and prepared for whatever comes my way.",
    rating: 5,
    tag: "First-Time Mom",
  },
  {
    id: "3",
    name: "Jessica L.",
    quote:
      "The support from my doula was incredible. She was there every step of the way, helping me advocate for myself and my baby.",
    rating: 5,
    tag: "Hospital Birth",
  },
  {
    id: "4",
    name: "Amanda K.",
    quote:
      "Birth Harmony transformed my birth experience. I went from fear to confidence, and had an amazing unmedicated birth.",
    rating: 5,
    tag: "VBAC",
  },
  {
    id: "5",
    name: "Michelle T.",
    quote:
      "Samantha's guidance through the hypnobirthing techniques helped me stay calm and focused during labor. I can't recommend her enough!",
    rating: 5,
    tag: "Second Baby",
  },
  {
    id: "6",
    name: "Rachel H.",
    quote:
      "The Birth Harmony course was the best investment we made for our pregnancy. My partner felt so prepared and involved.",
    rating: 5,
    tag: "Partner Support",
  },
];

export function useTestimonials() {
  const { content } = useContent();
  return content?.testimonials?.length
    ? content.testimonials
    : FALLBACK_TESTIMONIALS;
}

export function useFAQs() {
  const { content } = useContent();
  return content?.faqs || [];
}

export function useDoulaProfiles() {
  const { content } = useContent();
  return content?.["doula-profiles"] || [];
}

export function usePartnerProfiles() {
  const { content } = useContent();
  return content?.["partner-profiles"] || [];
}

// Fallback site settings when API is unavailable
const FALLBACK_SITE_SETTINGS: import("@shared/types/content").SiteSettings = {
  contact: {
    phone: "",
    email: "samantha@theaustindoula.com",
    address: "Austin, Texas",
  },
  social: {
    facebook: "https://facebook.com/theaustindoula",
    instagram: "https://instagram.com/theaustindoula",
    pinterest: "",
  },
  calendly: {
    homeBirthConsultation:
      "https://calendly.com/connectwithsamantha/home-birth-consultation",
    doulaConnection:
      "https://calendly.com/connectwithsamantha/doula-connection",
  },
};

export function useSiteSettings() {
  const { content } = useContent();
  return content?.["site-settings"] || FALLBACK_SITE_SETTINGS;
}
